<script setup lang="ts">
import {
  onActivated,
  onUnmounted,
  onMounted,
  computed,
  watch,
  nextTick,
  type Ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import MissionDisponiveis from './items/mission-cards/MissionDisponiveis.vue';
import MissionParticipando from './items/mission-cards/MissionParticipando.vue';
import MissionConcluidos from './items/mission-cards/MissionConcluidos.vue';
import MissionExpirados from './items/mission-cards/MissionExpirados.vue';
import MissionIndisponiveis from './items/mission-cards/MissionIndisponiveis.vue';
import MissionReward from './items/MissionReward.vue';
import PerfilOficialSwiper from '@/components/perfil-oficial/Swiper.vue';
import {
  getMissionStatus,
  getMissionStatusRank as getStatusRank,
  type MissionStatus,
} from '~/composables/useMissionButton';

const ITEMS_PER_LOAD = 9;

const { debugLog } = useSmarticoConfigs();
const route = useRoute();
const router = useRouter();
const routeParamMissionType = ref<string | undefined>(
  (route.query.type as string | undefined)?.toUpperCase(),
);

// Flag para evitar re-render quando está saindo da rota
const isLeavingRoute = ref(false);

watch(
  () => [route.path, route.query.type], // Watch both path and query.type
  ([newPath, newQueryType]) => {
    if (newPath !== '/vip/missions') {
      // Marca que está saindo da rota para evitar layout shift
      isLeavingRoute.value = true;
      // Não limpa routeParamMissionType para manter o layout atual
    } else {
      isLeavingRoute.value = false;
      routeParamMissionType.value = (
        newQueryType as string | undefined
      )?.toUpperCase();
      // If on the missions page and query type changes, reload missions
      loadMissions();
    }
  },
);

const {
  missions,
  optInMissions: rawOptInMissions,
  alreadyParticipatingMissions: rawAlreadyParticipatingMissions,
  claimedMissions: rawClaimedMissions,
  expiredMissions: rawExpiredMissions,
  lockedMissions: rawLockedMissions,
} = storeToRefs(useSmarticoMissions());

const authStore = useAuthStore();
const { isAuthenticated } = storeToRefs(authStore);

const ALBUM_CUSTOM_SECTION_ID = 1541;
const ALBUM_TAB_VALUE = 6;

function isAlbumMission(mission: TMissionOrBadge): boolean {
  return mission?.custom_section_id === ALBUM_CUSTOM_SECTION_ID;
}

// Missões da tab "Figurinhas" são exibidas exclusivamente nessa tab,
// portanto removidas dos demais arrays derivados do store.
function excludeAlbum(source: Ref<TMissionOrBadge[]>) {
  return computed(() => source.value.filter((m) => !isAlbumMission(m)));
}

const optInMissions = excludeAlbum(rawOptInMissions);
const alreadyParticipatingMissions = excludeAlbum(
  rawAlreadyParticipatingMissions,
);

const claimedMissions = excludeAlbum(rawClaimedMissions);
const expiredMissions = excludeAlbum(rawExpiredMissions);
const lockedMissions = excludeAlbum(rawLockedMissions);

const albumMissions = computed(() => {
  return [...missions.value]
    .filter((m) => isAlbumMission(m))
    .sort((a, b) => {
      const rankA = getStatusRank(a);
      const rankB = getStatusRank(b);

      if (rankA !== rankB) return rankA - rankB;

      const aNeedsClaim = a.requires_prize_claim && !a.prize_claimed_date_ts;
      const bNeedsClaim = b.requires_prize_claim && !b.prize_claimed_date_ts;

      if (aNeedsClaim && !bNeedsClaim) return -1;
      if (!aNeedsClaim && bNeedsClaim) return 1;

      return a.position - b.position;
    });
});

const hasAlbumMissions = computed(() => albumMissions.value.length > 0);

// Quando deslogado, "participating" aparece junto com "optIn" como disponíveis
const mergedAvailableMissions = computed(() => {
  if (!isAuthenticated.value) {
    // Merge optIn + participating e ordena por position
    return [...optInMissions.value, ...alreadyParticipatingMissions.value].sort(
      (a, b) => {
        const posA = a.position || 0;
        const posB = b.position || 0;

        if (!posA && !posB) return 0;
        if (!posA) return 1;
        if (!posB) return -1;

        return posA - posB;
      },
    );
  }

  return optInMissions.value;
});

// Quando deslogado, não mostra seção de "participando" separada
const showParticipatingSection = computed(() => {
  return isAuthenticated.value && alreadyParticipatingMissions.value.length > 0;
});

const filteredOptInMissions = computed(() =>
  mergedAvailableMissions.value.filter(matchesMissionType),
);

const filteredAlreadyParticipatingMissions = computed(() => {
  // Quando deslogado, não mostra seção separada de participando
  if (!isAuthenticated.value) return [];

  return alreadyParticipatingMissions.value.filter(matchesMissionType);
});

const filteredClaimedMissions = computed(() =>
  claimedMissions.value.filter(matchesMissionType),
);

const filteredExpiredMissions = computed(() =>
  expiredMissions.value.filter(matchesMissionType),
);

const filteredLockedMissions = computed(() =>
  lockedMissions.value.filter(matchesMissionType),
);

function matchesMissionType(mission: TMissionOrBadge) {
  if (!routeParamMissionType.value) return true;

  const status = getMissionStatus(mission);

  // Mapeia query params para status
  const typeToStatusMap: Record<string, MissionStatus[]> = {
    DISPONIVEIS: ['optIn'],
    PARTICIPANDO: ['participating'],
    CONCLUIDOS: ['claimable', 'completed'],
    EXPIRADOS: ['expired'],
    INDISPONIVEIS: ['locked'],
  };

  const allowedStatuses = typeToStatusMap[routeParamMissionType.value];

  // Se for um status conhecido, filtra por status
  if (allowedStatuses) {
    return allowedStatuses.includes(status);
  }

  // Caso contrário, filtra pelo custom_data (ex: MISSOESDODIA)
  // Exclui missões locked e expired quando filtrando por custom_data
  if (status === 'locked' || status === 'expired') {
    return false;
  }

  // Verifica se o custom_data contém o tipo solicitado
  let missionDataItems: string[] = [];

  if (mission.custom_data) {
    if (Array.isArray(mission.custom_data)) {
      missionDataItems = mission.custom_data.map(String);
    } else {
      missionDataItems = String(mission.custom_data)
        .split(',')
        .map((item) => item.trim());
    }
  }

  // Se não tem custom_data, não mostra
  if (missionDataItems.length === 0) {
    return false;
  }

  // Verifica se algum item do custom_data corresponde ao tipo
  return missionDataItems.some(
    (item) => item.toUpperCase() === routeParamMissionType.value,
  );
}

const {
  loadMissions,
  unregisterCustomUpdateCallback,
  registerCustomUpdateCallback,
} = useSmarticoMissions();

// Passamos os métodos específicos que precisamos: getMissions
const { isReady, initialLoadComplete, user } = useSmarticoUserReady(undefined, [
  'getMissions',
]);

// Função utilitária para normalizar strings (remover acentos e deixar minúsculo)
function normalizeString(str: string): string {
  return str
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036F]/g, '')
    .toLowerCase();
}

// Mapeamento de componente por status
const missionComponentMap: Record<MissionStatus, Component> = {
  claimable: MissionConcluidos,
  optIn: MissionDisponiveis,
  participating: MissionParticipando,
  completed: MissionConcluidos,
  expired: MissionExpirados,
  locked: MissionIndisponiveis,
};

// Função unificada para obter o componente correto
function getMissionComponent(mission: TMissionOrBadge) {
  const status = getMissionStatus(mission);

  // Quando deslogado, "participating" usa o mesmo componente de "optIn" (disponíveis)
  if (!isAuthenticated.value && status === 'participating') {
    return missionComponentMap.optIn;
  }

  return missionComponentMap[status];
}

function handleBackNavigation() {
  if (typeof window !== 'undefined') {
    try {
      const originUrl = sessionStorage.getItem('casino_origin_url');

      // Clear the origin URL to prevent reuse on subsequent navigations
      sessionStorage.removeItem('casino_origin_url');

      if (originUrl && originUrl.includes('/games/')) {
        // Navigate back to the saved games page URL
        router.push(originUrl);

        return;
      }
    } catch (e) {
      // Ignore storage errors
    }
  }
  // Default to browser back if no origin URL is saved
  router.back();
}

const allRibbonTabs = computed(() => {
  const tabs = [
    { name: 'Todos', value: 0 },
    { name: 'Disponíveis', value: 1 },
    { name: 'Concluídos', value: 2 },
    { name: 'Participando', value: 3 },
    { name: 'Expirados', value: 4 },
    { name: 'Indisponíveis', value: 5 },
  ];

  // Só decide pela presença da tab Figurinhas depois que o load inicial
  // terminou, evitando o flick de a tab aparecer/sumir entre o primeiro
  // render e a resposta das missões.
  if (initialLoadComplete.value && hasAlbumMissions.value) {
    tabs.splice(1, 0, { name: 'Figurinhas', value: ALBUM_TAB_VALUE });
  }

  return tabs;
});

const ribbonTabs = computed(() =>
  allRibbonTabs.value.filter((tab) => {
    if (tab.value === 0) return true;

    // Antes do load inicial terminar, mantém todas visíveis pra evitar flick
    if (!initialLoadComplete.value) return true;

    if (tab.value === 1) return mergedAvailableMissions.value.length > 0;
    if (tab.value === 2) return claimedMissions.value.length > 0;
    if (tab.value === 3) return showParticipatingSection.value;
    if (tab.value === 4) return expiredMissions.value.length > 0;
    if (tab.value === 5) return lockedMissions.value.length > 0;

    return true;
  }),
);

// Lógica para selecionar tab por query param (aceita value ou name)
const getTabFromQuery = () => {
  let tabFromQuery: string | undefined;

  for (const key in route.query) {
    if (key.toLowerCase() === 'tab') {
      tabFromQuery = route.query[key] as string;
      break;
    }
  }
  if (tabFromQuery) {
    const normalizedQuery = normalizeString(tabFromQuery);
    const validTab = ribbonTabs.value.find((tab) => {
      return (
        normalizeString(String(tab.value)) === normalizedQuery ||
        normalizeString(tab.name) === normalizedQuery
      );
    });

    if (validTab) {
      return validTab.value;
    }
  }

  return 0;
};

const ribbonTab = ref<number>(getTabFromQuery());

// Atualiza ribbonTab se a query mudar (mas não se for igual)
watch(
  () => route.query.tab,
  (newTab) => {
    const normalizedQuery = normalizeString(String(newTab || ''));
    const validTab = ribbonTabs.value.find(
      (tab) =>
        normalizeString(String(tab.value)) === normalizedQuery ||
        normalizeString(tab.name) === normalizedQuery,
    );

    if (validTab && ribbonTab.value !== validTab.value) {
      ribbonTab.value = validTab.value;
    }
    if (!newTab && ribbonTab.value !== 0) {
      ribbonTab.value = 0;
    }
  },
);

// Scroll to top e scroll horizontal para tab ativa quando muda de tab
watch(ribbonTab, () => {
  nextTick(() => {
    scrollEverythingToTopSmooth();
    scrollActiveTabIntoView();
  });
  // Limpar busca quando muda de tab
  search.value = '';
  debouncedSearch.value = '';
  isSearchExpanded.value = false;
});

// Se a tab ativa sumir (categoria ficou vazia após reload), volta pra "Todos"
watch(ribbonTabs, (newTabs) => {
  if (!newTabs.some((tab) => tab.value === ribbonTab.value)) {
    ribbonTab.value = 0;
  }
});

// Função para trocar a tab via query param (usada no click)
function setTabByQuery(tabValue: number) {
  ribbonTab.value = tabValue;
  missionsToShow.value = ITEMS_PER_LOAD;

  // Reset swiper visible counts
  optInVisible.value = ITEMS_PER_LOAD;
  participatingVisible.value = ITEMS_PER_LOAD;
  claimedVisible.value = ITEMS_PER_LOAD;
  expiredVisible.value = ITEMS_PER_LOAD;
  lockedVisible.value = ITEMS_PER_LOAD;

  const tabObj = ribbonTabs.value.find((t) => t.value === tabValue);
  const tabParam = tabObj
    ? normalizeString(tabObj.name)
    : normalizeString(String(tabValue));

  if (normalizeString(String(route.query.tab || '')) !== tabParam) {
    router.replace({
      query: {
        ...route.query,
        tab: tabParam,
      },
    });
  }
}

const tabsContainerRef = ref<HTMLElement | null>(null);

const optInSwiperRef = ref<InstanceType<typeof PerfilOficialSwiper> | null>(
  null,
);

const participatingSwiperRef = ref<InstanceType<
  typeof PerfilOficialSwiper
> | null>(null);

const completedSwiperRef = ref<InstanceType<typeof PerfilOficialSwiper> | null>(
  null,
);

const expiredSwiperRef = ref<InstanceType<typeof PerfilOficialSwiper> | null>(
  null,
);

const lockedSwiperRef = ref<InstanceType<typeof PerfilOficialSwiper> | null>(
  null,
);

const sortName = ref<string>('order_status');
const search = ref<string>('');
const debouncedSearch = ref<string>('');
const isSearchExpanded = ref<boolean>(false);
const searchInputRef = ref<HTMLInputElement | null>(null);
let searchDebounceTimeout: ReturnType<typeof setTimeout> | null = null;

const missionsContainerRef = ref<HTMLElement | null>(null);
const smarticoPageRef = ref<HTMLElement | null>(null);
const sentinelRef = ref<HTMLElement | null>(null);
const isMissionModalVisible = ref<boolean>(false);
const selectedMission = ref<TMissionOrBadge | null>(null);
const isMissionsLoading = ref<boolean>(false);
const isEventsInitialized = ref<boolean>(false);
const callbackId = 'missions-component-callback';
const missionsToShow = ref<number>(ITEMS_PER_LOAD);
let observer: IntersectionObserver | null = null;

const optInVisible = ref(ITEMS_PER_LOAD);
const participatingVisible = ref(ITEMS_PER_LOAD);
const claimedVisible = ref(ITEMS_PER_LOAD);
const expiredVisible = ref(ITEMS_PER_LOAD);
const lockedVisible = ref(ITEMS_PER_LOAD);

function loadMoreMissions() {
  missionsToShow.value += ITEMS_PER_LOAD;
}

function toggleSearch() {
  isSearchExpanded.value = !isSearchExpanded.value;
  if (isSearchExpanded.value) {
    nextTick(() => {
      searchInputRef.value?.focus();
    });
  } else {
    search.value = '';
    debouncedSearch.value = '';
  }
}

function handleSearchInput() {
  if (searchDebounceTimeout) {
    clearTimeout(searchDebounceTimeout);
  }
  searchDebounceTimeout = setTimeout(() => {
    debouncedSearch.value = search.value;
  }, 400);
}

function clearSearch() {
  search.value = '';
  debouncedSearch.value = '';
  searchInputRef.value?.focus();
}

function closeSearch() {
  isSearchExpanded.value = false;
  search.value = '';
  debouncedSearch.value = '';
}

function scrollActiveTabIntoView() {
  try {
    const container = tabsContainerRef.value;

    if (!container) return;

    const activeButton = container.querySelector(
      '[class*="activeTab"]',
    ) as HTMLElement;

    if (activeButton) {
      // Calcula a posição para centralizar a tab ativa
      const scrollLeft =
        activeButton.offsetLeft -
        container.offsetWidth / 2 +
        activeButton.offsetWidth / 2;

      container.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth',
      });
    }
  } catch {}
}

function scrollEverythingToTopSmooth() {
  try {
    // Janela
    if (
      typeof window !== 'undefined' &&
      typeof window.scrollTo === 'function'
    ) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  } catch {}
  try {
    // Document
    if (typeof document !== 'undefined') {
      (document.documentElement as any).scrollTop = 0;
      (document.body as any).scrollTop = 0;
    }
  } catch {}
  // Contêineres com overflow (subindo na hierarquia a partir do root)
  try {
    let node = smarticoPageRef.value as HTMLElement | null;

    while (node) {
      const style = window.getComputedStyle(node);
      const overflowY = style?.overflowY;

      if (
        (overflowY === 'auto' ||
          overflowY === 'scroll' ||
          overflowY === 'overlay') &&
        node.scrollHeight > node.clientHeight
      ) {
        try {
          if (typeof (node as any).scrollTo === 'function') {
            (node as any).scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            (node as any).scrollTop = 0;
          }
        } catch {}
      }
      node = node.parentElement as HTMLElement | null;
    }
  } catch {}
}

function getAllMissions() {
  return missions.value.filter((mission: TMissionOrBadge) => {
    if (mission.only_in_custom_section) return false;
    if (isAlbumMission(mission)) return false;

    return true;
  });
}

// Função para verificar se a missão corresponde à busca
function matchesSearch(mission: TMissionOrBadge, searchTerm: string): boolean {
  const lowerSearchTerm = searchTerm.toLowerCase();

  // Busca em name
  if (mission.name?.toString().toLowerCase().includes(lowerSearchTerm)) {
    return true;
  }

  // Busca em description
  if (mission.description?.toString().toLowerCase().includes(lowerSearchTerm)) {
    return true;
  }

  // Busca em reward (pode ser string ou objeto)
  if (mission.reward) {
    const rewardStr =
      typeof mission.reward === 'string'
        ? mission.reward
        : JSON.stringify(mission.reward);

    if (rewardStr.toLowerCase().includes(lowerSearchTerm)) {
      return true;
    }
  }

  return false;
}

const orderedMissions = computed(() => {
  let missionList = [];

  if (ribbonTab.value === 1) {
    // DISPONÍVEIS: usa mergedAvailableMissions quando deslogado
    missionList = mergedAvailableMissions.value;
  } else if (ribbonTab.value === 2) {
    missionList = claimedMissions.value;
  } else if (ribbonTab.value === 3) {
    // PARTICIPANDO: vazio quando deslogado (já estão em DISPONÍVEIS)
    missionList = isAuthenticated.value
      ? alreadyParticipatingMissions.value
      : [];
  } else if (ribbonTab.value === 4) {
    missionList = expiredMissions.value;
  } else if (ribbonTab.value === 5) {
    missionList = lockedMissions.value;
  } else if (ribbonTab.value === ALBUM_TAB_VALUE) {
    missionList = albumMissions.value;
  } else {
    missionList = getAllMissions();
  }

  // Filtro de busca com debounce - apenas para tabs diferentes de "TODOS" (tab 0)
  if (ribbonTab.value !== 0 && debouncedSearch.value.length >= 3) {
    missionList = missionList.filter((mission: TMissionOrBadge) =>
      matchesSearch(mission, debouncedSearch.value),
    );
  }

  if (sortName.value === 'order_name_asc') {
    return [...missionList].sort(
      (a, b) => a.name.localeCompare(b.name) || a.position - b.position,
    );
  }
  if (sortName.value === 'order_name_desc') {
    return [...missionList].sort(
      (a, b) => b.name.localeCompare(a.name) || a.position - b.position,
    );
  }

  const sortedMissions = [...missionList].sort((a, b) => {
    const rankA = getStatusRank(a);
    const rankB = getStatusRank(b);

    if (rankA !== rankB) {
      return rankA - rankB;
    }

    const aNeedsClaim = a.requires_prize_claim && !a.prize_claimed_date_ts;
    const bNeedsClaim = b.requires_prize_claim && !b.prize_claimed_date_ts;

    if (aNeedsClaim && !bNeedsClaim) return -1;
    if (!aNeedsClaim && bNeedsClaim) return 1;

    return a.position - b.position;
  });

  return sortedMissions;
});

// Computed centralizado para verificar se há missões para exibir
const hasNoMissionsToShow = computed(() => {
  if (!initialLoadComplete.value || isMissionsLoading.value) return false;

  // Se tem routeParamMissionType, verifica missões filtradas
  if (routeParamMissionType.value) {
    return (
      filteredOptInMissions.value.length === 0 &&
      filteredAlreadyParticipatingMissions.value.length === 0 &&
      filteredClaimedMissions.value.length === 0 &&
      filteredExpiredMissions.value.length === 0 &&
      filteredLockedMissions.value.length === 0
    );
  }

  // Se está em uma tab específica (não "TODOS"), verifica orderedMissions
  if (ribbonTab.value !== 0) {
    return orderedMissions.value.length === 0;
  }

  // Tab "TODOS": verifica todas as categorias (usando merged para disponíveis)
  return (
    mergedAvailableMissions.value.length === 0 &&
    (isAuthenticated.value
      ? alreadyParticipatingMissions.value.length === 0
      : true) &&
    claimedMissions.value.length === 0 &&
    expiredMissions.value.length === 0 &&
    lockedMissions.value.length === 0
  );
});

function openMissionModal(mission: TMissionOrBadge) {
  selectedMission.value = mission;
  isMissionModalVisible.value = true;
}

function clearOpenQueryParam() {
  const newQuery = { ...route.query };

  delete newQuery.open;
  router.replace({ query: newQuery });
}

function tryOpenMissionDeepLink() {
  const openParam = route.query.open as string | undefined;

  if (!openParam) return;
  if (!initialLoadComplete.value || isMissionsLoading.value) return;

  const targetId = Number(openParam);

  clearOpenQueryParam();

  if (!Number.isFinite(targetId)) return;

  const mission = (missions.value || []).find(
    (m: any) => Number(m?.id) === targetId,
  );

  if (mission) {
    openMissionModal(mission);
  }
}

watch(
  [() => route.query.open, initialLoadComplete, isMissionsLoading],
  () => tryOpenMissionDeepLink(),
  { immediate: true },
);

async function handleMissionOptedIn(missionId: number) {
  const { enterMission } = useSmarticoMissions();

  await enterMission(missionId);
}

function handleMissionsUpdate(updatedMissions: TMissionOrBadge[]) {
  if (!updatedMissions || !Array.isArray(updatedMissions)) {
    debugLog('⚠️ onUpdate callback received invalid data for missions');

    return;
  }
  debugLog(
    `🔄 Missions updated via callback onUpdate: ${updatedMissions.length} missions`,
  );
  loadMissions();
}

handleMissionsUpdate.id = callbackId;

async function start() {
  isMissionsLoading.value = true;
  await loadMissions();
  isMissionsLoading.value = false;
  initializeSmarticoEvents();
}

function initializeSmarticoEvents() {
  if (
    import.meta.client &&
    window &&
    window._smartico &&
    !isEventsInitialized.value
  ) {
    window._smartico.on('mission_completed', () => {
      debugLog('Event mission_completed detected - updating data');
      loadMissions();
    });
    isEventsInitialized.value = true;
  }
}

// Wait for user and Smartico API to be ready before initializing missions
watch(
  isReady,
  async (ready) => {
    if (ready) {
      debugLog('User and Smartico ready, initializing missions...');
      await start();
      initialLoadComplete.value = true;
    }
  },
  { immediate: true },
);

onActivated(() => {
  if (user.value && user.value.id) {
    registerCustomUpdateCallback(handleMissionsUpdate);
  }
});

onMounted(() => {
  if (import.meta.client) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            orderedMissions.value.length > missionsToShow.value
          ) {
            loadMoreMissions();

            // Em telas muito altas/largas (ex: 4K) o sentinel pode continuar
            // visível depois de carregar o lote, e o IntersectionObserver só
            // re-dispara quando a interseção muda (sai e volta à viewport).
            // Re-observamos o sentinel no próximo tick para forçar uma nova
            // avaliação e seguir carregando até ele sair de vista ou a lista
            // esgotar (quando esgota, o v-if desmonta o sentinel e para).
            if (orderedMissions.value.length > missionsToShow.value) {
              nextTick(() => {
                if (observer && sentinelRef.value) {
                  observer.unobserve(sentinelRef.value);
                  observer.observe(sentinelRef.value);
                }
              });
            }
          }
        });
      },
      { threshold: 0.9 },
    );
  }
});

// Watch for sentinel to appear and observe it
watch(
  () => sentinelRef.value,
  (newSentinel) => {
    if (newSentinel && observer) {
      observer.observe(newSentinel);
    }
  },
);

onUnmounted(() => {
  unregisterCustomUpdateCallback(handleMissionsUpdate);
  if (observer) {
    observer.disconnect();
  }
  if (searchDebounceTimeout) {
    clearTimeout(searchDebounceTimeout);
  }
});
</script>

<template>
  <div
    ref="smarticoPageRef"
    :class="$style.smarticoPageMissions"
  >
    <BaseScreenHeader
      heading-tag="h1"
      :on-back="handleBackNavigation"
      title="Missões"
    >
      <template #right>
        <div
          v-if="ribbonTab !== 0"
          :class="[
            $style.searchContainer,
            { [$style.expanded]: isSearchExpanded },
          ]"
        >
          <div :class="$style.searchInputWrapper">
            <span :class="$style.searchIconDesktop">
              <NuxtIcon name="search" />
            </span>
            <input
              ref="searchInputRef"
              v-model="search"
              :class="$style.searchInput"
              placeholder="Pesquisar missões..."
              type="text"
              @input="handleSearchInput"
              @keyup.escape="closeSearch"
            />
            <button
              v-if="search.length > 0"
              aria-label="Limpar busca"
              :class="$style.clearButton"
              @click="clearSearch"
            >
              <NuxtIcon name="close" />
            </button>
          </div>
          <button
            aria-label="Buscar missões"
            :class="$style.searchButton"
            @click="toggleSearch"
          >
            <NuxtIcon
              :name="isSearchExpanded ? 'arrows/angle_down' : 'search'"
            />
          </button>
        </div>
      </template>
    </BaseScreenHeader>

    <div
      v-if="!routeParamMissionType"
      ref="tabsContainerRef"
      :class="$style.tabs"
    >
      <BaseFilterTabs
        :model-value="ribbonTab"
        :tabs="ribbonTabs"
        @update:model-value="setTabByQuery($event as number)"
      />
    </div>

    <!-- Quando está saindo da rota, mantém o skeleton para evitar layout shift -->
    <div
      v-if="isLeavingRoute"
      class="flex items-center justify-center min-h-[200px] opacity-50"
    >
      <SmarticoApostatudoSkeletonSwiperSectionSkeleton
        v-if="routeParamMissionType"
        :card-height="200"
        :card-min-width="240"
        :cards-per-section="4"
        :sections="3"
      />
      <SmarticoApostatudoSkeletonMissionsSkeleton
        v-else
        :count="6"
      />
    </div>

    <!-- Skeleton apenas no conteúdo (header e tabs permanecem visíveis) -->
    <template v-else-if="!initialLoadComplete">
      <!-- Tab "Todos" usa skeleton com seções de swiper -->
      <SmarticoApostatudoSkeletonSwiperSectionSkeleton
        v-if="ribbonTab === 0 || routeParamMissionType"
        :card-height="200"
        :card-min-width="240"
        :cards-per-section="4"
        :sections="5"
      />
      <!-- Outras tabs usam skeleton de grid -->
      <SmarticoApostatudoSkeletonMissionsSkeleton
        v-else
        :count="6"
      />
    </template>

    <template v-else>
      <div v-if="routeParamMissionType">
        <div
          v-if="filteredOptInMissions.length > 0"
          :class="$style.missionsContainer"
        >
          <PerfilOficialSwiper
            ref="optInSwiperRef"
            icon-path="mission/section/disponiveis"
            see-more-url="/vip/missions?tab=disponiveis"
            title="Disponíveis"
          >
            <component
              :is="getMissionComponent(mission)"
              v-for="mission in filteredOptInMissions"
              :key="mission.id"
              :is-featured="mission.dt_start !== null"
              :mission="mission"
              @mission-opted-in="handleMissionOptedIn"
              @open-mission-modal="openMissionModal"
            />
          </PerfilOficialSwiper>
        </div>

        <div
          v-if="filteredClaimedMissions.length > 0"
          class="mt-4"
          :class="$style.missionsContainer"
        >
          <PerfilOficialSwiper
            ref="completedSwiperRef"
            icon-path="mission/section/concluidos"
            see-more-url="/vip/missions?tab=concluidos"
            title="Concluídos"
          >
            <component
              :is="getMissionComponent(mission)"
              v-for="mission in filteredClaimedMissions"
              :key="mission.id"
              :is-featured="mission.dt_start !== null"
              :mission="mission"
              @mission-opted-in="handleMissionOptedIn"
              @open-mission-modal="openMissionModal"
            />
          </PerfilOficialSwiper>
        </div>

        <div
          v-if="filteredAlreadyParticipatingMissions.length > 0"
          class="mt-4"
          :class="$style.missionsContainer"
        >
          <PerfilOficialSwiper
            ref="participatingSwiperRef"
            icon-path="mission/section/participando"
            see-more-url="/vip/missions?tab=participando"
            title="Participando"
          >
            <component
              :is="getMissionComponent(mission)"
              v-for="mission in filteredAlreadyParticipatingMissions"
              :key="mission.id"
              :is-featured="mission.dt_start !== null"
              :mission="mission"
              @mission-opted-in="handleMissionOptedIn"
              @open-mission-modal="openMissionModal"
            />
          </PerfilOficialSwiper>
        </div>

        <div
          v-if="filteredExpiredMissions.length > 0"
          class="mt-4"
          :class="$style.missionsContainer"
        >
          <PerfilOficialSwiper
            ref="expiredSwiperRef"
            icon-path="mission/section/expirados"
            see-more-url="/vip/missions?tab=expirados"
            title="Expirados"
          >
            <component
              :is="getMissionComponent(mission)"
              v-for="mission in filteredExpiredMissions"
              :key="mission.id"
              :is-featured="mission.dt_start !== null"
              :mission="mission"
              @mission-opted-in="handleMissionOptedIn"
              @open-mission-modal="openMissionModal"
            />
          </PerfilOficialSwiper>
        </div>

        <div
          v-if="filteredLockedMissions.length > 0"
          class="mt-4"
          :class="$style.missionsContainer"
        >
          <PerfilOficialSwiper
            ref="lockedSwiperRef"
            icon-path="mission/section/indisponiveis"
            see-more-url="/vip/missions?tab=indisponiveis"
            title="Indisponíveis"
          >
            <component
              :is="getMissionComponent(mission)"
              v-for="mission in filteredLockedMissions"
              :key="mission.id"
              :is-featured="mission.dt_start !== null"
              :mission="mission"
              @mission-opted-in="handleMissionOptedIn"
              @open-mission-modal="openMissionModal"
            />
          </PerfilOficialSwiper>
        </div>
      </div>

      <div
        v-else-if="route.path === '/vip/missions' && ribbonTab !== 0"
        ref="missionsContainerRef"
        :class="$style.gridItems"
      >
        <template v-if="orderedMissions.length > 0">
          <component
            :is="getMissionComponent(mission)"
            v-for="mission in orderedMissions.slice(0, missionsToShow)"
            :key="mission.id"
            :is-featured="mission.dt_start !== null"
            :mission="mission"
            @mission-opted-in="handleMissionOptedIn"
            @open-mission-modal="openMissionModal"
          />
          <div
            v-if="orderedMissions.length > missionsToShow"
            ref="sentinelRef"
            class="h-4"
          ></div>
        </template>
      </div>

      <!-- Secoes para a aba Todas -->
      <div v-if="ribbonTab === 0 && !routeParamMissionType">
        <div
          v-if="mergedAvailableMissions.length > 0"
          :class="$style.missionsContainer"
        >
          <PerfilOficialSwiper
            ref="optInSwiperRef"
            icon-path="mission/section/disponiveis"
            see-more-url="/vip/missions?tab=disponiveis"
            title="Disponíveis"
            @near-end="
              () => {
                if (optInVisible < mergedAvailableMissions.length)
                  optInVisible += ITEMS_PER_LOAD;
              }
            "
          >
            <component
              :is="getMissionComponent(mission)"
              v-for="mission in mergedAvailableMissions.slice(0, optInVisible)"
              :key="mission.id"
              :is-featured="mission.dt_start !== null"
              :mission="mission"
              @mission-opted-in="handleMissionOptedIn"
              @open-mission-modal="openMissionModal"
            />
          </PerfilOficialSwiper>
        </div>

        <div
          v-if="claimedMissions.length > 0"
          class="mt-4"
          :class="$style.missionsContainer"
        >
          <PerfilOficialSwiper
            ref="completedSwiperRef"
            icon-path="mission/section/concluidos"
            see-more-url="/vip/missions?tab=concluidos"
            title="Concluídos"
            @near-end="
              () => {
                if (claimedVisible < claimedMissions.length)
                  claimedVisible += ITEMS_PER_LOAD;
              }
            "
          >
            <component
              :is="getMissionComponent(mission)"
              v-for="mission in claimedMissions.slice(0, claimedVisible)"
              :key="mission.id"
              :is-featured="mission.dt_start !== null"
              :mission="mission"
              @mission-opted-in="handleMissionOptedIn"
              @open-mission-modal="openMissionModal"
            />
          </PerfilOficialSwiper>
        </div>

        <div
          v-if="showParticipatingSection"
          class="mt-4"
          :class="$style.missionsContainer"
        >
          <PerfilOficialSwiper
            ref="participatingSwiperRef"
            icon-path="mission/section/participando"
            see-more-url="/vip/missions?tab=participando"
            title="Participando"
            @near-end="
              () => {
                if (participatingVisible < alreadyParticipatingMissions.length)
                  participatingVisible += ITEMS_PER_LOAD;
              }
            "
          >
            <component
              :is="getMissionComponent(mission)"
              v-for="mission in alreadyParticipatingMissions.slice(
                0,
                participatingVisible,
              )"
              :key="mission.id"
              :is-featured="mission.dt_start !== null"
              :mission="mission"
              @mission-opted-in="handleMissionOptedIn"
              @open-mission-modal="openMissionModal"
            />
          </PerfilOficialSwiper>
        </div>

        <div
          v-if="expiredMissions.length > 0"
          class="mt-4"
          :class="$style.missionsContainer"
        >
          <PerfilOficialSwiper
            ref="expiredSwiperRef"
            icon-path="mission/section/expirados"
            see-more-url="/vip/missions?tab=expirados"
            title="Expirados"
            @near-end="
              () => {
                if (expiredVisible < expiredMissions.length)
                  expiredVisible += ITEMS_PER_LOAD;
              }
            "
          >
            <component
              :is="getMissionComponent(mission)"
              v-for="mission in expiredMissions.slice(0, expiredVisible)"
              :key="mission.id"
              :is-featured="mission.dt_start !== null"
              :mission="mission"
              @mission-opted-in="handleMissionOptedIn"
              @open-mission-modal="openMissionModal"
            />
          </PerfilOficialSwiper>
        </div>

        <div
          v-if="lockedMissions.length > 0"
          class="mt-4"
          :class="$style.missionsContainer"
        >
          <PerfilOficialSwiper
            ref="lockedSwiperRef"
            icon-path="mission/section/indisponiveis"
            see-more-url="/vip/missions?tab=indisponiveis"
            title="Indisponíveis"
            @near-end="
              () => {
                if (lockedVisible < lockedMissions.length)
                  lockedVisible += ITEMS_PER_LOAD;
              }
            "
          >
            <component
              :is="getMissionComponent(mission)"
              v-for="mission in lockedMissions.slice(0, lockedVisible)"
              :key="mission.id"
              :is-featured="mission.dt_start !== null"
              :mission="mission"
              @mission-opted-in="handleMissionOptedIn"
              @open-mission-modal="openMissionModal"
            />
          </PerfilOficialSwiper>
        </div>
      </div>

      <!-- Warning centralizado para quando não há missões -->
      <div
        v-if="hasNoMissionsToShow"
        class="alert alert-warning"
      >
        <span>
          {{ $t('components.search_bar.no_results') }}
        </span>
      </div>

      <h1 class="text-white/60 text-center my-8 px-4 leading-tight">
        Missões VIP Apostatudo: objetivos e recompensas para jogadores
      </h1>
    </template>

    <MissionReward
      v-if="selectedMission"
      v-model:modal-visible="isMissionModalVisible"
      :mission="selectedMission"
    />
  </div>
</template>

<style module lang="postcss">
.smarticoPageMissions {
  @apply mb-4 w-full -mt-3 lg:-mt-1;

  .gridItems {
    display: grid;
    /* Mobile: min 150px por card, cresce automaticamente */
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 10px;
    width: 100%;
    max-width: 100%;
    /* Isola contexto de composição para evitar fragmentos visuais de blur/shadow */
    isolation: isolate;
  }

  /* Alert ocupa linha toda */
  .gridItems > :global(.alert) {
    grid-column: 1 / -1;
  }

  /* Desktop: min 240px por card */
  @media (min-width: 640px) {
    .gridItems {
      grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    }
  }

  .loadingContainer {
    @apply flex items-center justify-center min-h-[200px];

    & > * {
      animation: none !important;
    }
  }

  .gridItems > *:not(:only-child) {
    animation: shuffle-card 0.7s ease-in-out;
    animation-fill-mode: forwards;
    width: 100%;
    min-width: 0;
  }

  .missionsContainer {
    animation: shuffle-card 0.7s ease-in-out;
    animation-fill-mode: forwards;
    /* Isola contexto de composição para evitar fragmentos visuais de blur/shadow */
    isolation: isolate;
    contain: layout style paint;
  }
}

/* Animação apenas para os cards do grid */
@keyframes shuffle-card {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.tabs {
  @apply mt-2 mb-4 border-b border-[#424344] select-none;
}
/* Searchbar expansível */
.searchContainer {
  @apply flex items-center relative;
  transition: all 0.3s ease;
}

.searchButton {
  @apply flex items-center justify-center w-9 h-9 rounded-full bg-transparent text-texts/60 hover:text-texts transition-colors;
  z-index: 2;

  svg {
    @apply text-lg;
  }

  /* Esconde o botão no desktop */
  @media (min-width: 1024px) {
    @apply hidden;
  }
}

.searchInputWrapper {
  @apply flex items-center overflow-hidden;
  width: 0;
  opacity: 0;
  transition:
    width 0.3s ease,
    opacity 0.3s ease;

  /* No desktop, sempre visível */
  @media (min-width: 1024px) {
    width: 250px;
    opacity: 1;
  }
}

.expanded .searchInputWrapper {
  width: 190px;
  opacity: 1;
}

.searchIconDesktop {
  @apply flex items-center justify-center absolute left-2 text-black/50;
  z-index: 2;

  svg {
    @apply text-base;
  }
}

.searchInput {
  @apply w-full h-8 pl-8 pr-3 bg-[#fff] border border-[#30364d] rounded-lg text-black text-sm placeholder-black/50;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #00c9ff;
  }
}

.clearButton {
  @apply absolute right-10 flex items-center justify-center w-5 h-5 text-black/40 hover:text-black transition-colors;

  svg {
    @apply text-xs;
  }

  /* No desktop, ajusta posição pois não há botão de busca */
  @media (min-width: 1024px) {
    @apply right-2;
  }
}
</style>
